import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';

import type {
  ColSize,
  IColConfig,
  IBaseTableProps,
  ITableProps,
  IConfigurableList,
} from 'src/models/IConfigurableList';

import { Preloader } from '@nebo-team/vobaza.ui.preloader/dist';
import { TableRowWithDnD, TableRowWithSelect, TableRow } from './TableRow';
import TableHead from './TableHead';

import styles from './styles.module.scss';

enum ColsSizes {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
}

const colsSizesMap = {
  [ColsSizes.Small]: 60,
  [ColsSizes.Medium]: 120,
  [ColsSizes.Large]: 220,
};

const getTemplateCols = (sizes: Array<ColSize | number | undefined> = []) => {
  const result: Array<number | string> = [];

  sizes.forEach((s) => {
    const defaultValue = colsSizesMap[ColsSizes.Medium];

    switch (typeof s) {
      case 'string': {
        if (s.includes('%')) {
          result.push(s);
          break;
        }

        result.push(
          colsSizesMap[s as keyof typeof colsSizesMap] || defaultValue
        );
        break;
      }
      case 'number':
        result.push(s);
        break;
      default:
        defaultValue;
    }
  });

  return result
    .map((t) => {
      const strinVal = String(t);
      if (strinVal.includes('%')) return t;
      else return strinVal + 'px';
    })
    .join(' ');
};

const Table: FC<ITableProps> = (props) => {
  const {
    flat,
    busy,
    withoutSidebar,
    children,
    className,
    loadMore = () => {},
    hasMore,
    tableScroll,
  } = props;

  let tableClass = flat ? styles.tableFlat : styles.table;

  if (withoutSidebar) {
    tableClass = tableClass + ' ' + styles.tableFull;
  }

  if (tableScroll) {
    tableClass = tableClass + ' ' + styles.tableScroll;
  }

  return (
    <InfiniteScroll
      hasMore={hasMore}
      className={[className, tableClass].join(' ')}
      loadMore={loadMore}
      useWindow={false}
      loader={<Preloader />}
    >
      {children}
      {busy && (
        <div className={styles.tablePreloader}>
          <Preloader />
        </div>
      )}
    </InfiniteScroll>
  );
};

interface ITableBodyProps {
  children: ReactNode;
  emptyMsg: string | ReactNode;
}

const TableBody: FC<ITableBodyProps> = (props) => {
  const { children, emptyMsg = 'Нет элементов' } = props;

  const isEmpty = !Array.isArray(children) || !children.length;

  return (
    <div className={styles.tableBody}>
      {!isEmpty && children}
      {isEmpty && <div className={styles.tableEmptyMsg}>{emptyMsg}</div>}
    </div>
  );
};

const BaseTable: FC<IBaseTableProps> = (props) => {
  const {
    keyField = 'id',
    name = '',
    rowGridTemplate = '',
    busy = false,
    withoutSidebar = false,
    flat = false,
    list,
    cols,
    emptyMsg,
    className,
    isAllSelected,
    selected,
    selectable = false,
    canChangeRowsSort = false,
    onClickCheckbox,
    onClickRow,
    onChangeRowsOrder,
    loadMore,
    hasMore,
    withoutGap = false,
    tableScroll = false,
  } = props;

  const RowComponent = useMemo(() => {
    let component = TableRow;

    if (selectable) {
      component = TableRowWithSelect(component);
    }

    if (canChangeRowsSort) {
      component = TableRowWithDnD(component);
    }

    return component;
  }, [selectable, canChangeRowsSort]);

  const commonRowProps = {
    cols,
    keyField,
    gridTemplate: rowGridTemplate,
    tableScroll,
  } as any;

  if (selectable)
    Object.assign(commonRowProps, { onClickCheckbox, onClickRow });
  if (canChangeRowsSort)
    Object.assign(commonRowProps, { tableName: name, onChangeRowsOrder });
  if (withoutGap) Object.assign(commonRowProps, { withoutGap });

  const renderTableRow = (item: any) => {
    const computedRowProps = { key: Math.random(), data: item } as any;

    if (selectable) {
      computedRowProps.isSelected =
        !!selected && selected!.includes(item[keyField]);
    }

    return <RowComponent {...commonRowProps} {...computedRowProps} />;
  };

  return (
    <Table
      busy={busy}
      flat={flat}
      withoutSidebar={withoutSidebar}
      className={className}
      loadMore={loadMore}
      hasMore={hasMore}
      tableScroll={tableScroll}
    >
      <TableHead
        cols={cols}
        rowGridTemplate={rowGridTemplate}
        selectable={selectable}
        isAllSelected={isAllSelected}
        canChangeRowsSort={canChangeRowsSort}
        onClickCheckbox={onClickCheckbox}
        withoutGap={withoutGap}
      />
      <TableBody emptyMsg={emptyMsg}>
        {list && list.map(renderTableRow)}
      </TableBody>
    </Table>
  );
};

function TableWithSelect(TableComponent: FC<IBaseTableProps>) {
  return function WrappedTable({ ...props }: IBaseTableProps) {
    const { list, selected, onChangeSelected, selectable } = props;

    const [selectedItems, setSelectedItems] = useState<(string | number)[]>([]);
    const [isAllSelected, setIsAllSelected] = useState(false);

    useEffect(() => {
      if (selected) {
        setSelectedItems(selected);
        setIsAllSelected(!!selected.length && selected.length === list.length);
      }
    }, [selected, list]);

    const handleSelectItem = (keyFieldValue: string | number) => {
      return (value: boolean) => {
        let newSelected = [...selectedItems];
        if (value) {
          newSelected.push(keyFieldValue);

          if (newSelected.length === list.length) {
            setIsAllSelected(true);
          }
        } else {
          newSelected = newSelected.filter((t) => t !== keyFieldValue);
          setIsAllSelected(false);
        }

        onChangeSelected && onChangeSelected(newSelected);
      };
    };

    const handleChangeIsAllSelected = (value: boolean) => {
      if (value) {
        onChangeSelected && onChangeSelected(list.map((t) => t.id));
        setIsAllSelected(true);
      } else {
        setIsAllSelected(false);
        onChangeSelected && onChangeSelected([]);
      }
    };

    const handleClickCheckbox = (keyFieldValue: string | number) => {
      if (keyFieldValue === 'ALL') return handleChangeIsAllSelected;

      return handleSelectItem(keyFieldValue);
    };

    const handleClickRow = (keyFieldValue: string | number, value: boolean) => {
      if (!selectable) return;
      if (keyFieldValue === 'ALL') return handleChangeIsAllSelected(!value);

      let newSelected = [...selectedItems];

      if (!value) {
        newSelected.push(keyFieldValue);

        if (newSelected.length === list.length) {
          setIsAllSelected(true);
        }
      } else {
        newSelected = newSelected.filter((t) => t !== keyFieldValue);
        setIsAllSelected(false);
      }

      onChangeSelected && onChangeSelected(newSelected);
    };

    return (
      <TableComponent
        {...props}
        selectable
        selected={selectedItems}
        isAllSelected={isAllSelected}
        onClickCheckbox={handleClickCheckbox}
        onClickRow={handleClickRow}
      />
    );
  };
}

function TableWithChangeOrder(TableComponent: FC<IBaseTableProps>) {
  return function WrappedTable({ ...props }: IBaseTableProps) {
    return (
      <DndProvider backend={HTML5Backend}>
        <TableComponent {...props} canChangeRowsSort />
      </DndProvider>
    );
  };
}

const ConfigurableList: FC<IConfigurableList> = (props) => {
  if (!Array.isArray(props.cols)) props.cols = [];

  const [TableComponent, rowGridTemplate] = useMemo(() => {
    let component = BaseTable;
    let gridTemplate = getTemplateCols(props.cols.map((t) => t.size));

    if (props.selectable) {
      component = TableWithSelect(component);
      gridTemplate = '25px ' + gridTemplate;
    }

    if (props.canChangeRowsSort) {
      component = TableWithChangeOrder(component);
      gridTemplate = '20px ' + gridTemplate;
    }

    return [component, gridTemplate];
  }, [props.selectable, props.canChangeRowsSort, props.cols]);

  return <TableComponent {...props} rowGridTemplate={rowGridTemplate} />;
};

export { ConfigurableList };
export type { IColConfig };
